import React from 'react';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FieldValues, SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { ContactJsonldTiersReadNoteGroupTimestampableBlameambleGroup } from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { useSelector } from 'react-redux';
import { SelectOptions } from '@europrocurement/l2d-modules/modules/Offres/forms/types';
import SelectField from '@europrocurement/l2d-modules/modules/Offres/components/FormInputs/SelectField';
import { ContactTiersRead, useSociete, useTiersService } from '@europrocurement/l2d-domain';
import { FlexyButton, ModalContext, useL2DTheme } from '@europrocurement/flexy-components';
import { useSnackbar } from 'notistack';
import { OptionsSelector } from '../../../redux';
import { PrescriberFormProps } from './common';
import {
    ContactFormValues,
    contactSchema,
    formToContactCreationPayload,
    formToContactUpdatePayload,
    getContactFormInitialValues,
    formPhonesToPayload,
    formEmailsToPayload,
} from './form';
import OptionalContactList from './OptionalContactList';

export type ContactFormProps = PrescriberFormProps & {
    contact?: ContactJsonldTiersReadNoteGroupTimestampableBlameambleGroup;
};

// Static layout constants — no dependency on component state or props

const gridLine = { xs: 12, sm: 12, md: 12, lg: 12 } as const;
const gridHalf = { ...gridLine, md: 6, lg: 6 } as const;

const formHeader: FormStructure = {
    type: 'customItem',
    sx: { marginTop: '0px' },
    name: 'info',
    renderField: () => (
        <Typography
            fontWeight={500}
            fontSize="24px"
        >
            Informations du contact
        </Typography>
    ),
};

const horizontalDivider: FormStructure = {
    type: 'customItem',
    name: 'horizontalDivider',
    renderField: () => (
        <Box sx={{ marginY: '32px', opacity: 0.3 }}>
            <hr />
        </Box>
    ),
};

const spacer: FormStructure = {
    type: 'customItem',
    name: 'spacer',
    renderField: () => <Box sx={{ marginY: '20px' }} />,
};

const ContactForm: React.FunctionComponent<ContactFormProps> = function ({
    afterSubmit,
    contact,
    prescriber,
}) {
    const { civilities } = useSelector(OptionsSelector);
    const prescriberId = prescriber?.id;
    const contactId = contact?.id;
    const isCreationForm = !contact;

    const { enqueueSnackbar } = useSnackbar();
    const { prescribersApi, contactsApi } = useTiersService();
    const { modalActions } = React.useContext(ModalContext);

    const { breakpoints } = useL2DTheme();
    const mdBreakpoint = useMediaQuery(breakpoints.down('md'));
    const xIdSociete = useSociete();

    const formContext = useForm({
        mode: 'onTouched',
        defaultValues: getContactFormInitialValues(contact),
        resolver: zodResolver(contactSchema),
    });

    const {
        control,
        watch,
        formState: { errors },
    } = formContext;

    const extractServerError = (error: unknown): string | undefined => {
        const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
        if (!data) return undefined;
        return (
            (data['hydra:description'] as string | undefined) ??
            (data.detail as string | undefined) ??
            (data.exception as { message?: string } | undefined)?.message
        );
    };

    /**
     * Proceed contact update
     *
     * The contact update calls three different api endpoints:
     * - 1. Contact main informations
     * - 2. Contact phones
     * - 3. Contact emails
     *
     * All three must succeed. On any failure, a snackbar error is shown with
     * the contact name for context and the server detail when available.
     */
    const updateContact: SubmitHandler<ContactFormValues> = async (formValues) => {
        if (typeof contactId === 'undefined' || typeof prescriberId === 'undefined') return;

        try {
            await Promise.all([
                contactsApi.updateContactContactsItem({
                    xIdSociete,
                    id: contactId.toString(),
                    contactsContactJsonldContactUpdate: formToContactUpdatePayload(formValues),
                }),
                contactsApi.updateTelephonesContactPrescripteurContactsItem({
                    xIdSociete,
                    id: contactId.toString(),
                    tiers: prescriberId.toString(),
                    contactsContactJsonldContactWriteTelsPrescripteur: formPhonesToPayload(
                        contact as ContactTiersRead,
                        formValues,
                    ),
                }),
                contactsApi.updateEmailsContactPrescripteurContactsItem({
                    xIdSociete,
                    id: contactId.toString(),
                    tiers: prescriberId.toString(),
                    contactsContactJsonldContactWriteEmailsPrescripteur: formEmailsToPayload(
                        contact as ContactTiersRead,
                        formValues,
                    ),
                }),
            ]);
            formContext.reset();
            afterSubmit();
        } catch (error) {
            const contactName = `${formValues.firstName} ${formValues.lastName}`;
            const serverDetail = extractServerError(error);
            const message = serverDetail
                ? `La mise à jour de ${contactName} a échoué : ${serverDetail}`
                : `La mise à jour de ${contactName} a échoué.`;
            enqueueSnackbar(message, { variant: 'error', autoHideDuration: 15000 });
        }
    };

    const createContact: SubmitHandler<ContactFormValues> = async (formValues) => {
        if (!prescriber || !prescriberId) return;

        try {
            await prescribersApi.createPrescripteurContactContactsCollection({
                xIdSociete,
                tiers: prescriberId,
                contactsContactJsonldContactCreate: formToContactCreationPayload(
                    contact as ContactTiersRead,
                    formValues,
                ),
            });
            formContext.reset();
            afterSubmit();
        } catch (error) {
            const contactName = `${formValues.firstName} ${formValues.lastName}`;
            const serverDetail = extractServerError(error);
            const message = serverDetail
                ? `La création du contact ${contactName} a échoué : ${serverDetail}`
                : `La création du contact ${contactName} a échoué.`;
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    const submitForm: SubmitHandler<ContactFormValues> = async (formValues) => {
        if (isCreationForm) {
            await createContact(formValues);
        } else {
            await updateContact(formValues);
        }
    };

    // RENDER / Structure & style

    const civilitiesOptions: SelectOptions = [
        ...civilities.map((apiCivility) => ({
            label: apiCivility.libelle ?? '( Aucune valeur )',
            value: apiCivility.id,
        })),
        { label: 'Non renseignée', value: 0 },
    ];

    const generalInformationsSection: FormStructure[] = [
        {
            type: 'customItem',
            name: 'civilityId',
            ...gridLine,
            renderField: () => (
                <Controller
                    name="civilityId"
                    control={control}
                    render={({ field }) => (
                        <SelectField
                            {...field}
                            value={watch(field.name)}
                            name={field.name}
                            label="Civilité"
                            options={civilitiesOptions}
                            errors={errors}
                        />
                    )}
                />
            ),
        },
        {
            type: 'text',
            name: 'lastName',
            inputlabel: 'Nom',
            rules: { required: 'Vous devez renseigner un nom' },
            ...gridHalf,
        },
        {
            type: 'text',
            name: 'firstName',
            inputlabel: 'Prénom',
            rules: { required: 'Vous devez renseigner un prénom' },
            ...gridHalf,
        },
        {
            type: 'text',
            name: 'position',
            inputlabel: 'Fonction',
            ...gridLine,
        },
    ];

    const contactsSection: FormStructure[] = [
        {
            type: 'text',
            name: 'mainPhone',
            inputlabel: 'Téléphone principal',
            rules: { required: 'Vous devez renseigner un numéro de téléphone' },
            ...gridLine,
        },
        {
            type: 'customItem',
            name: 'optionalPhones',
            ...gridLine,
            renderField: () => (
                <Box>
                    <OptionalContactList
                        type="phone"
                        context={formContext}
                    />
                </Box>
            ),
        },
        {
            type: 'text',
            name: 'mainEmail',
            inputlabel: 'E-mail principal',
            rules: { required: 'Vous devez renseigner un e-mail' },
            ...gridLine,
        },
        {
            type: 'customItem',
            name: 'optionalEmails',
            ...gridLine,
            renderField: () => (
                <Box sx={{ marginTop: '7px' }}>
                    <OptionalContactList
                        type="email"
                        context={formContext}
                    />
                </Box>
            ),
        },
    ];

    const contactSettingsSection: FormStructure[] = [
        {
            type: 'selectItems',
            name: 'isAdmin',
            mode: 'inline',
            inputlabel: 'Rôle',
            colInline: 6,
            items: [
                { label: 'Administrateur', value: true },
                { label: 'Collaborateur', value: false },
            ],
            multiple: false,
            rules: {},
        },
        spacer,
        {
            type: 'boolean',
            name: 'isMainContact',
            inputlabel: 'Contact principal',
            sx: { marginTop: '15px' },
            ...gridLine,
        },
        spacer,
        {
            type: 'boolean',
            name: 'isBillingContact',
            inputlabel: 'Contact comptabilité',
            ...gridLine,
        },
        spacer,
        {
            type: 'boolean',
            name: 'hasLeft',
            inputlabel: 'À quitté',
            inputSublabel: 'Le contact à quitté la société',
            display: !isCreationForm,
            ...gridLine,
        },
    ];

    const formStructure: FormStructure[] = [
        formHeader,
        ...generalInformationsSection,
        horizontalDivider,
        ...contactsSection,
        horizontalDivider,
        ...contactSettingsSection,
    ];

    const submitLabel = isCreationForm ? 'Créer un nouveau contact' : 'Mettre à jour le contact';

    return (
        <Box sx={{ maxWidth: mdBreakpoint ? '100%' : '60%', margin: 'auto' }}>
            <FlexyForm
                formContext={formContext as unknown as UseFormReturn<FieldValues>}
                formStructure={formStructure}
                onSubmit={submitForm}
                submitButton={{
                    render: ({ isSubmitting, isLoading, isValidating }) => (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                gap: '32px',
                                paddingTop: '32px',
                            }}
                        >
                            <FlexyButton
                                type="button"
                                variant="contained"
                                color="error"
                                onClick={() => modalActions.close()}
                            >
                                Annuler
                            </FlexyButton>
                            <FlexyButton
                                type="submit"
                                disabled={isSubmitting || isLoading || isValidating}
                                variant="contained"
                            >
                                {submitLabel}
                            </FlexyButton>
                        </Box>
                    ),
                }}
            />
        </Box>
    );
};

export default ContactForm;
